import React, { useEffect, useState } from "react";

import {
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
} from "@mui/material";
import { Clear, Download, Refresh } from "@mui/icons-material";
import {
  DataGrid,
  getDefaultGridFilterModel,
  GridActionsCellItem,
  GridLogicOperator,
  useGridApiRef,
} from "@mui/x-data-grid";

import { saveAs } from "file-saver";

import { API_URL, CONSUMER_URL, EXTRACTION_URL } from "../../helpers/variables";
import FileViewer from "./FileViewer";
import { useLoaderData } from "react-router-dom";

import {
  AutoComplete,
  DatePicker,
  Popover,
  Select,
  Button as AButton,
  Divider,
} from "antd";
import dayjs from "dayjs";

import utc from "dayjs/plugin/utc";
import { enqueueSnackbar, SnackbarProvider } from "notistack";

dayjs.extend(utc);

export default function FileQueue() {
  const { PMSList } = useLoaderData();

  const apiRef = useGridApiRef();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const [filterModal, setFilterModal] = useState(getDefaultGridFilterModel());

  const [sourceForm, setSourceForm] = useState({
    dateRange: [dayjs(), dayjs()],
    pms: "",
    sourceType: "upload",
  });
  const { dateRange, pms, sourceType } = sourceForm;

  const [fileData, setFileData] = useState({});
  const { fileList = [], totalFiles = 0 } = fileData;

  const [selectedFile, setSelectedFile] = useState();
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [corporations, setCorporations] = useState([]);
  const [isSaleFetching, setIsSaleFetching] = useState(false);
  const [searchSaleForm, setSearchSaleForm] = useState({});

  useEffect(() => {
    getCorporations();
  }, []);

  useEffect(() => {
    if (!pms) {
      enqueueSnackbar("Please select PMS");
    }

    getFileData();

    if (!autoRefresh) return;

    const interval = setInterval(() => {
      getFileData();
    }, 5000);

    return () => clearInterval(interval);
  }, [
    sourceForm,
    // paginationModel,
    autoRefresh,
  ]);

  async function getCorporations() {
    setLoading(true);
    let res = await fetch(`${API_URL}/get_all_corporations?return_all=true`);
    let data = await res.json();
    setCorporations(data);
    setLoading(false);
  }

  const groupedCorporations = Object.values(
    corporations.reduce((groups, item) => {
      const clientName = item.clientName;

      if (!groups[clientName]) {
        groups[clientName] = {
          label: clientName,
          options: [],
        };
      }

      groups[clientName].options.push({
        value: item.id,
        label: item.pc ? item.pcName : item.name,
      });

      return groups;
    }, {}),
  );

  async function getFileData() {
    let [startDate, endDate] = dateRange || [];
    setLoading(true);
    let params = new URLSearchParams({
      source_type: sourceType,
      pms,
      start_date: startDate.startOf("day").utc().format(),
      end_date: endDate.endOf("day").utc().format(),
      page_size: paginationModel.pageSize,
      page: paginationModel.page,
    });
    let res = await fetch(`${API_URL}/get_file_queue?${params}`);
    let data = await res.json();
    setFileData(data);
    setLoading(false);
  }

  async function openExtractionPage(file) {
    setLoading(true);
    let res = await fetch(
      `${CONSUMER_URL}/get_preassigned_url?s3_key=${file["s3_key"]}`,
    );
    setSelectedFile({ ...file, preassigned_url: await res.text() });
    setLoading(false);
  }

  async function downloadFile(s3Key, filename) {
    setLoading(true);
    let res = await fetch(`${CONSUMER_URL}/dowload_file?s3_key=${s3Key}`);
    let blob = await res.blob();
    saveAs(blob, filename);
    setLoading(false);
  }

  async function downloadPayload(sale_id) {
    setLoading(true);
    let res = await fetch(`${API_URL}/download_payload?sale_id=${sale_id}`);
    let blob = await res.blob();
    saveAs(blob, `payload_${sale_id}.json`);
    setLoading(false);
  }

  async function searchSale() {
    let { corporationID, reportDate } = searchSaleForm;

    setIsSaleFetching(true);
    let res = await fetch(
      `${API_URL}/get_sale_details?corporation_id=${
        corporationID
      }&report_date=${encodeURIComponent(reportDate.format("MM/DD/YYYY"))}`,
    );
    setIsSaleFetching(false);

    if (res.status !== 200) {
      enqueueSnackbar(await res.text());
    } else {
      let data = await res.json();
      let receivedDate = dayjs(objectIdToDate(data._id));
      setSourceForm((prev) => ({
        ...prev,
        pms: data.pms_name,
        dateRange: [receivedDate, receivedDate],
        sourceType: data.initial_source,
      }));
      let corporation = corporations.find((i) => i.id === corporationID);
      setFilterModal({
        items: [
          {
            field: "report_date",
            operator: "is",
            value: reportDate.format("YYYY-MM-DD"),
          },
        ],
        quickFilterValues: [
          corporation.pc ? corporation.pcName : corporation.name,
        ],
      });
    }
  }

  function getDefaultRowSpanValue(row) {
    return row._id;
  }

  function getSaleRowSpanValue(row) {
    let sale_id = row.sale_id;
    return sale_id ? row.source_id + sale_id : getDefaultRowSpanValue(row);
  }

  function updateSourceForm(k, v) {
    setSourceForm((prev) => ({ ...prev, [k]: v }));
  }

  function saleForm() {
    return (
      <Stack gap={2} p={1}>
        <Select
          options={groupedCorporations}
          value={searchSaleForm.corporationID || undefined}
          onChange={(value) =>
            setSearchSaleForm((p) => ({
              ...p,
              corporationID: value,
            }))
          }
          placeholder="Select corporation or pc"
          showSearch={{
            optionFilterProp: "label",
          }}
        />
        <DatePicker
          format={"MM/DD/YYYY"}
          value={searchSaleForm.reportDate || null}
          onChange={(d) => setSearchSaleForm((p) => ({ ...p, reportDate: d }))}
        />
        <AButton
          disabled={!searchSaleForm.corporationID || !searchSaleForm.reportDate}
          onClick={searchSale}
          loading={isSaleFetching}
        >
          Search
        </AButton>
      </Stack>
    );
  }

  return (
    <Stack height={"100%"}>
      <SnackbarProvider />
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Stack direction={"row"} gap={2} alignItems={"center"}>
          <Tabs
            value={sourceType}
            onChange={(e, v) => updateSourceForm("sourceType", v)}
          >
            <Tab label="Upload" value={"upload"} />
            <Tab label="Email" value={"mail"} />
          </Tabs>

          <TextField
            select
            value={pms}
            onChange={(e) => updateSourceForm("pms", e.target.value)}
            sx={{ minWidth: 250 }}
            size="small"
            label="PMS"
          >
            {PMSList.map((item, index) => (
              <MenuItem key={index} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>

          <DatePicker.RangePicker
            value={dateRange}
            onChange={(d) => updateSourceForm("dateRange", d)}
            size="large"
            allowClear={false}
            format={"MM/DD/YYYY"}
            presets={[
              {
                label: "Today",
                value: [dayjs().startOf("day"), dayjs().endOf("day")],
              },
              {
                label: "Yesterday",
                value: [
                  dayjs().subtract(1, "day").startOf("day"),
                  dayjs().subtract(1, "day").endOf("day"),
                ],
              },
              {
                label: "This Week",
                value: [dayjs().startOf("week"), dayjs().endOf("day")],
              },
            ]}
          />

          <Divider orientation="vertical" />

          <Popover
            content={saleForm}
            trigger={"click"}
            onOpenChange={(open) => {
              if (!open) {
                setSearchSaleForm({});
              }
            }}
            styles={{
              content: {
                width: Math.max(...corporations.map((c) => c.name.length * 8)),
              },
            }}
          >
            <AButton variant="filled" color="primary" size="large">
              Sale Lookup
            </AButton>
          </Popover>
        </Stack>

        <Stack direction={"row"} gap={2}>
          {!autoRefresh && (
            <Button
              size="small"
              endIcon={<Refresh />}
              variant="contained"
              onClick={getFileData}
              disabled={!pms}
            >
              Refresh
            </Button>
          )}
          <FormControlLabel
            checked={autoRefresh}
            onChange={(e, c) => setAutoRefresh(c)}
            control={<Switch size="small" />}
            label="Auto refresh"
          />
        </Stack>
      </Toolbar>

      <DataGrid
        apiRef={apiRef}
        getRowId={(row) => row._id}
        rows={fileList.map((row, index) => ({
          ...row,
          sno: index + 1,
        }))}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnResize={autoRefresh}
        disableColumnSorting
        // disableColumnFilter
        // rowCount={totalFiles}
        // paginationMode="server"
        // pageSizeOptions={[15]}
        filterModel={filterModal}
        onFilterModelChange={setFilterModal}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => setPaginationModel(m)}
        showToolbar
        showCellVerticalBorder
        showColumnVerticalBorder
        rowSpanning
        columns={[
          // { field: "sno", headerName: "S. No", width: 60 },
          {
            field: "timestamp",
            headerName: "Received at",
            flex: 1.5,
            filterable: false,
            display: "flex",
            valueGetter: (v, r, c) => dayjs(v).format("MM/DD/YYYY HH:mm:ss"),
            rowSpanValueGetter: (_, row) => row.source_id,
          },
          {
            field: "source_filename",
            headerName: "Filename",
            flex: 2,
            rowSpanValueGetter: (_, row) => getDefaultRowSpanValue(row),
          },
          {
            field: "status",
            headerName: "File Status",
            flex: 1.5,
            filterable: false,
            renderCell: ({ value, row }) => {
              let file = FileStatus[value];
              return (
                file && (
                  <Tooltip arrow title={row["description"]}>
                    <Chip
                      size="small"
                      label={file["description"]}
                      color={file["color"]}
                      onClick={() => openExtractionPage(row)}
                    />
                  </Tooltip>
                )
              );
            },
            rowSpanValueGetter: (_, row) => getDefaultRowSpanValue(row),
          },
          {
            field: "report_name",
            headerName: "Report Name",
            flex: 1,
            filterable: false,
            valueGetter: (v, r, c) => r.extraction?.report.name,
            rowSpanValueGetter: (_, row) => getDefaultRowSpanValue(row),
          },
          {
            field: "client",
            headerName: "Client",
            flex: 1,
            display: "flex",
            valueGetter: (v, r, c) => r.extraction?.client_name,
            rowSpanValueGetter: (_, row) => getSaleRowSpanValue(row),
          },
          {
            field: "corporation",
            headerName: "PC or Corporation",
            flex: 2,
            display: "flex",
            valueGetter: (v, r, c) => {
              let corporation = r.extraction?.corporation;
              if (!corporation) return;
              return corporation.pc_name || corporation.name;
            },
            rowSpanValueGetter: (_, row) => getSaleRowSpanValue(row),
          },
          {
            field: "report_date",
            headerName: "Report date",
            flex: 1,
            display: "flex",
            type: "date",
            valueGetter: (v, r) => {
              const date = r.extraction?.report_date?.date;
              return date ? new Date(date) : null;
            },
            valueFormatter: (value) =>
              value ? value.toLocaleDateString() : "",
            rowSpanValueGetter: (_, row) => getSaleRowSpanValue(row),
          },
          {
            field: "sale",
            headerName: "Sale Status",
            flex: 1.5,
            filterable: false,
            display: "flex",
            renderCell: ({ value, row }) => {
              let sale = SalePostingStatus[value];
              return (
                sale && (
                  <Chip
                    size="small"
                    label={sale["description"]}
                    color={sale["color"]}
                    onClick={() => downloadPayload(row.sale_id)}
                  />
                )
              );
            },
            rowSpanValueGetter: (_, row) => getSaleRowSpanValue(row),
          },
          {
            field: "actions",
            type: "actions",
            width: 50,
            getActions: ({ id, row }) => [
              <GridActionsCellItem
                icon={<Download />}
                label="Download"
                onClick={() =>
                  downloadFile(row["s3_key"], row["source_filename"])
                }
                color="inherit"
              />,
            ],
          },
        ]}
        sx={{
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "transparent",
          },
        }}
      />

      {/* Extraction view */}
      <Dialog fullScreen open={Boolean(selectedFile)}>
        <AppBar position="static">
          <Toolbar variant="dense">
            <Typography flexGrow={1}>
              {selectedFile?.["source_filename"]}
            </Typography>
            <IconButton onClick={() => setSelectedFile()}>
              <Clear />
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ display: "flex", gap: 1, p: 1 }}>
          {selectedFile && (
            <>
              <Box width={"40%"} border={1} borderColor={"divider"}>
                <FileViewer
                  filename={selectedFile["source_filename"]}
                  fileURL={selectedFile["preassigned_url"]}
                />
              </Box>
              <Box width={"60%"} border={1} borderColor={"divider"}>
                <iframe
                  src={`${EXTRACTION_URL}/review_screen?extraction_id=${selectedFile["extraction_id"]}`}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

const FileStatus = {
  [-1]: {
    description: "Invalid File",
    color: "error",
  },
  [-2]: {
    description: "OCR Failed",
    color: "error",
  },
  [-3]: {
    description: "Extraction Failed",
    color: "error",
  },
  [0]: {
    description: "Pending",
    color: "warning",
  },
  [1]: {
    description: "Success",
    color: "success",
  },
  [2]: {
    description: "Duplicate File",
    color: "warning",
  },
  [3]: {
    description: "Deleted",
    color: "warning",
  },
  [4]: {
    description: "Unmapped Report",
    color: "warning",
  },
};

const SalePostingStatus = {
  [1]: {
    description: "Success",
    color: "success",
  },
  [0]: {
    description: "On Hold",
    color: "warning",
  },
  [2]: {
    description: "Manually posted",
    color: "primary",
  },
  [3]: {
    description: "Deleted",
    color: "default",
  },
  [4]: {
    description: "Duplicate",
    color: "warning",
  },
  [-1]: {
    description: "Posting Failed",
    color: "error",
  },
};

function objectIdToDate(objectId) {
  if (!objectId) return null;
  const timestamp = parseInt(objectId.substring(0, 8), 16);
  return new Date(timestamp * 1000);
}
